"use client";

import { useEffect, useState } from "react";

import { cityAirports, type AirportOption } from "../_logic/airport-options";
import { loadAirportRecords, loadPlaceIndex } from "../_logic/place-catalog";
import type { AirportRecord } from "../_logic/places";

/** A home city's airports and the full catalog to search. */
export interface AirportCatalog {
  own: AirportOption[];
  airports: AirportRecord[];
}

/** Where the catalog load stands. */
export type AirportCatalogStatus = "loading" | "ready" | "error";

/**
 * Loads the airport catalog for one home city and keeps whether it is still loading or failed,
 * so the picker can tell "no airport matches" apart from "the airports didn't load".
 * @param city - The home city whose own airports come first.
 * @returns The catalog (null until loaded), its status, and `retry` to load it again after a failure.
 * @throws Never.
 */
export function useAirportCatalog(city: string): {
  catalog: AirportCatalog | null;
  status: AirportCatalogStatus;
  retry: () => void;
} {
  // States
  const [catalog, setCatalog] = useState<AirportCatalog | null>(null);
  const [status, setStatus] = useState<AirportCatalogStatus>("loading");
  const [attempt, setAttempt] = useState(0);

  // Handlers
  const retry = () => {
    setStatus("loading");
    setAttempt((current) => current + 1);
  };

  // Effects
  useEffect(() => {
    let cancelled = false;
    Promise.all([loadPlaceIndex(), loadAirportRecords()])
      .then(([index, airports]) => {
        if (cancelled) return;
        setCatalog({ own: cityAirports(city, index, airports), airports });
        setStatus("ready");
      })
      .catch(() => {
        if (!cancelled) setStatus("error");
      });
    return () => {
      cancelled = true;
    };
  }, [city, attempt]);

  return { catalog, status, retry };
}
